import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WaitlistForm = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');

    try {
      const response = await axios.post(`${API}/waitlist`, { email });

      if (response.data.success) {
        setStatus('success');
        toast.success(response.data.message);
        setEmail('');
        // Track conversion in Google Analytics
        if (window.gtag) {
          window.gtag('event', 'waitlist_signup', {
            event_category: 'conversion',
            event_label: 'waitlist',
          });
        }
      } else {
        setStatus('error');
        toast.error(response.data.message || 'Hubo un error. Intenta de nuevo.');
      }
    } catch {
      setStatus('error');
      toast.error('Hubo un error. Intenta de nuevo.');
    }

    // Reset status after a delay
    setTimeout(() => {
      setStatus((prev) => (prev === 'loading' ? prev : 'idle'));
    }, 4000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
      className="w-full max-w-md mx-auto"
      data-testid="waitlist-form-container"
    >
      <form onSubmit={handleSubmit} className="relative" data-testid="waitlist-form" autoComplete="on">
        <div className="flex items-center rounded-full border border-white/[0.15] bg-white/[0.06] backdrop-blur-2xl overflow-hidden transition-colors focus-within:border-[#FF4F00]/50 focus-within:bg-white/[0.08]">
          <input
            type="email"
            id="waitlist-email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            required
            disabled={status === 'loading'}
            data-testid="waitlist-email-input"
            className="flex-1 bg-transparent text-white text-sm sm:text-base placeholder:text-zinc-400 px-5 py-3.5 sm:py-4 outline-none font-normal tracking-wide disabled:opacity-50"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
          />
          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <motion.div
                key="success"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="mr-2 flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500"
                data-testid="waitlist-success-icon"
              >
                <Check className="w-5 h-5 text-white" />
              </motion.div>
            ) : (
              <motion.button
                key="submit"
                type="submit"
                disabled={status === 'loading' || !email.trim()}
                data-testid="waitlist-submit-button"
                className="mr-1.5 flex items-center gap-2 bg-[#FF4F00] text-white text-sm font-semibold px-6 py-2.5 sm:py-3 rounded-full whitespace-nowrap disabled:opacity-40 hover:bg-[#FF6B35] transition-colors"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  boxShadow: '0 0 20px rgba(255, 79, 0, 0.3), 0 0 40px rgba(255, 79, 0, 0.1)',
                }}
              >
                {status === 'loading' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <span>Obtener acceso</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </form>

      <AnimatePresence>
        {status === 'success' && (
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center text-sm text-emerald-400 mt-4 font-light"
            data-testid="waitlist-success-message"
          >
            ¡Estás en la lista! Te avisaremos pronto.
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default WaitlistForm;
